import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { useAuthContext } from '@picsart/growth-rc/AuthContext';
import useDevice from '@picsart/rc/useDevice';

import {
  FREE_TRIAL_TRIGGER,
  HEADER_MONETIZATION_BUTTON,
  HEADER_MONETIZATION_BUTTON_TEXT,
  REGISTRATION_POINT,
} from 'constants/homePage';
import useMonetizationDataStore from 'stores/monetizationDataStore';
import { getStorage, setStorage } from 'utils/crudStorage';
import { LandingCategory } from 'constants/enums';

import { configs } from 'utils/getConfigs';
import useSubscriptionStore from 'stores/useSubscriptionStore';
import { UserSubscriptionResponseType } from 'stores/types';
import usePageDataStore from '../stores/pageDataStore';
import usePageSlug from './usePageSlug';

const useRedirectToCreate = () => {
  const navigate = useNavigate();
  const { isDesktop } = useDevice();
  const { user, tokenData } = useAuthContext();
  const { languageParam, slug } = usePageSlug();
  const isHomePage = `/${slug}` === '/';
  const isClickedFromSpecialOffer = useMonetizationDataStore(x => x.isClickedFromSpecialOffer);
  const pageData = usePageDataStore(state => state.pageData?.data);
  const isMaximizer = pageData?.category === LandingCategory.maximizer;
  const { redirectTo, fetchUserSubscription } = useSubscriptionStore(store => ({
    fetchUserSubscription: store.fetchUserSubscription,
    redirectTo: store.redirectTo,
  }));

  useEffect(() => {
    if (
      (!isHomePage && !pageData?.onlyForGuests && !redirectTo) ||
      !configs.enableRedirections ||
      isClickedFromSpecialOffer
    )
      return;

    if (user) {
      const headerMonetizationButton = getStorage({ name: HEADER_MONETIZATION_BUTTON });
      const headerMonetizationButtonText =
        getStorage({ name: HEADER_MONETIZATION_BUTTON_TEXT }) || 'Log in';
      const monetizationPopupDone = getStorage({
        name: `${REGISTRATION_POINT.REGISTRATION_DONE}${user.id}`,
      });
      if (user?.registered && !headerMonetizationButton && monetizationPopupDone !== 'true') {
        setStorage({ name: REGISTRATION_POINT.COOKIE_NAME, value: REGISTRATION_POINT.HOME });
      }

      const lang = languageParam ? `/${languageParam}` : '';
      const url = `${lang}/create?page_origin=${headerMonetizationButtonText
        .replaceAll(' ', '_')
        .toLowerCase()}&action_button=registration&source=create`;

      if ((tokenData?.access_token && isMaximizer) || isHomePage) {
        fetchUserSubscription(tokenData?.access_token, pageData?.category, isHomePage).then(
          (resp: UserSubscriptionResponseType) => {
            const separator = redirectTo?.includes('?') ? '&' : '?';
            window.location.href = redirectTo
              ? `${redirectTo}${separator}maximizer=${resp?.isMaximizer}`
              : `${url}&maximizer=${resp?.isMaximizer}`;
          },
        );
      } else {
        if (isDesktop) {
          setStorage({
            name: FREE_TRIAL_TRIGGER,
            value: '0',
          });
        }
        window.location.href = url;
      }
    }
  }, [
    user,
    isDesktop,
    navigate,
    isHomePage,
    languageParam,
    isClickedFromSpecialOffer,
    pageData?.onlyForGuests,
    slug,
    tokenData?.access_token,
    redirectTo,
    fetchUserSubscription,
    isMaximizer,
    pageData?.category,
  ]);

  return isHomePage;
};

export default useRedirectToCreate;
